/**
 * Owner: Masa Bayu
 * File: paragraph-justified-face-object.tsx
 * Date:Time: 2026-10-12 19:45
 * Description: Fasa 8 · body paragraph · Stage-2b constrained path when flag ON.
 */

import type { CSSProperties, ReactElement } from 'react';
import { buildJustificationPlan } from './build-justification-data';
import { faceJustifyInstructions } from '../paint/face-justify-remeasure';
import type { NativeLine } from './group-native-paragraphs';
import { groupInstructionsIntoLines, JustifiedTextBlock } from './JustifiedTextBlock';
import { columnMetrics, nativeLineHeightPt } from './native-line-metrics';
import { FACE_BODY_INK, faceStackZIndex } from './qx-editor-page-face-objects';
import { resolveCssFontFamily } from '../fonts/spsu-fonts';
import type { AssetLibrary } from '../extract/spsu-types';

type Props = {
  readonly plates: readonly NativeLine[];
  readonly assets: AssetLibrary;
};

/**
 * Face body paragraph · absolute box + JustifiedTextBlock.
 * Indent baris pertama + line-height dari plat native (bukan 1.35 tetap).
 */
export function ParagraphJustifiedFaceObject({
  plates,
  assets,
}: Props): ReactElement | null {
  if (plates.length < 2) return null;
  const host = plates[0]!;
  const unit = host.bbox.unit;
  const { left, top, width, firstIndent } = columnMetrics(plates);
  const lineHeightPt = nativeLineHeightPt(plates);
  const lineHeightCss = lineHeightPt / Math.max(1, host.style.fontSize);

  const firstWidth = Math.max(1, width - firstIndent);
  const plan = buildJustificationPlan(plates, width);
  if (plan.instructions.length === 0) return null;

  const hostFamily = resolveCssFontFamily(host.style.fontFamily, assets);
  const fontCss = [
    host.style.fontStyle === 'italic' ? 'italic' : 'normal',
    host.style.fontWeight === 'normal' ? '400' : String(host.style.fontWeight),
    `${host.style.fontSize}pt`,
    hostFamily,
  ].join(' ');

  const constraints = plan.flowPlates.map((_, i) => (i === 0 ? firstWidth : width));
  // Constrained-C removed (B3) · legacy remasure via faceJustifyInstructions.
  const justified = faceJustifyInstructions(
    plan.tokens,
    plan.instructions,
    constraints,
    width,
    fontCss,
  );
  const lines = groupInstructionsIntoLines(justified);

  const box: CSSProperties = {
    position: 'absolute',
    left: `${left}${unit}`,
    top: `${top}${unit}`,
    width: `${width}${unit}`,
    zIndex: Math.max(...plates.map((p) => faceStackZIndex(p))),
    boxSizing: 'border-box',
    margin: 0,
    padding: 0,
    overflow: 'visible',
    fontFamily: hostFamily,
    fontSize: `${host.style.fontSize}${unit}`,
    fontWeight: host.style.fontWeight,
    fontStyle: host.style.fontStyle,
    color: FACE_BODY_INK,
    lineHeight: lineHeightCss,
  };

  return (
    <div
      className="qx-native-paragraph-justified"
      data-native-paragraph-justified="true"
      data-plate-ids={plates.map((p) => p.id).join(',')}
      data-qxeditor-element={host.id}
      data-first-indent={String(firstIndent)}
      data-face-justify-path="legacy"
      data-face-justify-eligible="na"
      style={box}
    >
      {lines.map((lineInst, lineIndex) => {
        const indented = lineIndex === 0 && firstIndent > 0.5;
        return (
          <div
            key={`para-line-${lineIndex}`}
            style={{
              marginLeft: indented ? `${firstIndent}${unit}` : 0,
              width: indented ? `${firstWidth}${unit}` : `${width}${unit}`,
            }}
          >
            <JustifiedTextBlock
              instructions={lineInst.map((inst, i) => ({
                ...inst,
                isLastInLine: i === lineInst.length - 1,
              }))}
              spacingUnit="pt"
            />
          </div>
        );
      })}
    </div>
  );
}
